import {
  useEffect,
  useMemo,
  useState,
  useCallback,
  type ReactNode,
} from 'react'
import { useNavigate, useParams, useBlocker } from 'react-router-dom'
import isEqual from 'lodash/isEqual'
import { StepNavigation } from './step-navigation'
import { StepDealOverview } from './steps/step-deal-overview'
import { StepProjectSummary } from './steps/step-project-summary'
import StepDealTerms from './steps/step-investment-terms'
import { StepFundAllocation } from './steps/step-fund-allocation'
import { StepSecondaryMarket } from './steps/step-secondary-market'
import { StepTimelinePlan } from './steps/step-timeline-plan'
import { StepLegalAgreements } from './steps/step-legal-agreements'
import { StepAccessoryServices } from './steps/step-accessory-services'
import { StepErc4626Vault } from './steps/step-erc4626-vault'
import { StepV1Deposit } from './steps/step-v1-deposit'
import { CheckCircle, CircleAlert, TriangleAlert } from 'lucide-react'
import { Card, CardContent } from '../../components/ui/card'
import {
  postDeal,
  mapFormToPayload,
  normalizeDealState,
  formatDealStateLabel,
  submitDeal,
} from '../../lib/deal-api'
import {
  getRejectReasonsForStep,
  getFieldLabel,
} from '../../lib/reject-reason-mapper'
import { getDefaultDealFormData } from '../../lib/form-defaults'
import type { DealFormData, ValidationError } from '../../lib/types'
import { Button } from '../../components/ui/button'
import { Dialog, DialogContent } from '../../../components/ui/dialog'
import {
  loadFormDraft,
  //saveFormDraft,
  clearFormDraft,
} from '../../lib/form-storage'
import useToast from 'hooks/useToast'
import { ERC4626_DEAL_TYPE, isV1ContractDealType } from '../../lib/constants'

const baseSteps = [
  {
    id: 1,
    title: 'Deal Overview',
    description:
      'Provide basic information about the project and deal structure.',
    component: StepDealOverview,
  },
  {
    id: 2,
    title: 'Project Overview',
    description:
      'Complete a research review of the Project to provide accurate due-diligence materials to investors.',
    component: StepProjectSummary,
  },
  {
    id: 3,
    title: 'Deal Terms',
    description: 'Define the investment structure and terms.',
    component: StepDealTerms,
  },
  {
    id: 4,
    title: 'Fund Allocation',
    description: 'Set total allocation and distribution rules for this deal.',
    component: StepFundAllocation,
  },
  {
    id: 5,
    title: 'Sale Timeline',
    description:
      'Define key dates for the purchase window and token generation.',
    component: StepTimelinePlan,
  },
  {
    id: 6,
    title: 'Trading & Royalties',
    description:
      'Allow NFTs to be traded on marketplaces and define how royalties are shared.',
    component: StepSecondaryMarket,
  },
  {
    id: 7,
    title: 'Legal Agreements',
    description:
      'The proposed sale will be reviewed by our team. If the submission is approved, you will be contacted to proceed with the execution of the relevant agreement. In the interim, please review the general sale agreement set out below for reference.',
    component: StepLegalAgreements,
  },
  {
    id: 8,
    title: 'Accessory Project Services',
    description:
      'Select additional services the project may require. Referral fees may apply if sourced by CURATED.',
    component: StepAccessoryServices,
  },
]

const erc4626Steps = [
  baseSteps[0],
  baseSteps[1],
  {
    id: 3,
    title: 'Vault Configuration',
    description:
      'Configure the ERC-4626 vault, underlying asset, and sale behavior.',
    component: StepErc4626Vault,
  },
]

const v1DepositSteps = [
  baseSteps[0],
  baseSteps[1],
  {
    id: 3,
    title: 'Sale Contract Configuration',
    description:
      'Configure the deployed v1 deposit sale: contract addresses, deposit, refund and claim windows, and timeline.',
    component: StepV1Deposit,
  },
]

const getStepsForSalesType = (salesType: string) => {
  if (salesType === ERC4626_DEAL_TYPE) return erc4626Steps
  if (isV1ContractDealType(salesType)) return v1DepositSteps
  const isOtcDeal = salesType === 'OTC Deal'
  return baseSteps.filter((step) => {
    if (isOtcDeal && step.id === 3) return false
    if (isOtcDeal && step.id === 6) return false
    return true
  })
}

const extractDealId = (result: any): string | null => {
  if (!result) return null
  if (typeof result === 'string' || typeof result === 'number')
    return String(result)
  if (typeof result.id === 'string' || typeof result.id === 'number')
    return String(result.id)
  const data = (result as any).data
  if (data) {
    if (typeof data === 'string' || typeof data === 'number')
      return String(data)
    if (typeof data.id === 'string' || typeof data.id === 'number')
      return String(data.id)
  }
  if (
    typeof (result as any).dealId === 'string' ||
    typeof (result as any).dealId === 'number'
  )
    return String((result as any).dealId)
  if (
    typeof (result as any).deal_id === 'string' ||
    typeof (result as any).deal_id === 'number'
  )
    return String((result as any).deal_id)
  return null
}

const tryParseJson = (value: string): unknown | null => {
  const trimmed = value.trim()
  if (!trimmed) return null
  if (
    !(trimmed.startsWith('{') && trimmed.endsWith('}')) &&
    !(trimmed.startsWith('[') && trimmed.endsWith(']'))
  )
    return null
  try {
    return JSON.parse(trimmed)
  } catch {
    return null
  }
}

const extractMessageFromUnknown = (data: unknown): string | null => {
  if (data === null || data === undefined) return null
  if (typeof data === 'string') {
    const trimmed = data.trim()
    return trimmed.length ? trimmed : null
  }
  if (typeof data === 'number' || typeof data === 'boolean') return String(data)
  if (Array.isArray(data)) {
    for (const item of data) {
      const message = extractMessageFromUnknown(item)
      if (message) return message
    }
    return null
  }
  if (typeof data === 'object') {
    const record = data as Record<string, unknown>
    const preferredKeys = [
      'message',
      'error',
      'detail',
      'description',
      'msg',
      'title',
      'reason',
    ]
    for (const key of preferredKeys) {
      if (key in record) {
        const message = extractMessageFromUnknown(record[key])
        if (message) return message
      }
    }
    for (const value of Object.values(record)) {
      const message = extractMessageFromUnknown(value)
      if (message) return message
    }
  }
  return null
}

const formatSubmissionError = (
  error: unknown,
  isEditMode: boolean,
): { heading: string; message: string } => {
  const fallbackMessage = isEditMode
    ? 'Failed to update the deal. Please try again.'
    : 'Failed to submit the deal. Please try again.'

  const extractFromMessage = (message: string): string | null => {
    const httpMatch = message.match(
      /^(POST|PUT)\s+failed:\s*(\d{3})([\s\S]*)$/i,
    )
    if (httpMatch) {
      const statusCode = Number(httpMatch[2])
      const bodyPart = httpMatch[3]?.trim() ?? ''
      if (statusCode === 401 || statusCode === 403)
        return 'You are not authorized to perform this action.'
      if (statusCode === 404)
        return 'The requested deal could not be found. Please refresh and try again.'
      if (statusCode >= 500)
        return 'The server encountered an issue. Please try again shortly.'
      if (bodyPart) {
        const parsed = tryParseJson(bodyPart)
        const extracted =
          extractMessageFromUnknown(parsed) ??
          extractMessageFromUnknown(bodyPart)
        if (extracted) return extracted
        return bodyPart
      }
      return `Request failed with status ${statusCode}.`
    }
    if (/network\s*error/i.test(message))
      return 'Network error: please check your connection or CORS settings.'
    const extracted = extractMessageFromUnknown(message)
    if (extracted) return extracted
    const trimmed = message.trim()
    return trimmed.length ? trimmed : null
  }

  if (error instanceof Error) {
    const message = error.message ?? ''
    const extracted = extractFromMessage(message)
    if (extracted) return { heading: 'Submission failed', message: extracted }
  } else if (typeof error === 'string') {
    const extracted = extractFromMessage(error)
    if (extracted) return { heading: 'Submission failed', message: extracted }
  }

  const extracted = extractMessageFromUnknown(error)
  if (extracted) return { heading: 'Submission failed', message: extracted }

  return { heading: 'Submission failed', message: fallbackMessage }
}

interface DealSubmissionFlowProps {
  mode?: 'create' | 'edit'
  initialData?: DealFormData | null
  onSubmit?: (payload: any, formData: DealFormData) => Promise<void>
  isLoading?: boolean
  submitButtonLabel?: string
  successScreenTitle?: string
  successScreenStatusText?: string
  successNextSteps?: ReactNode[]
  enableDraftPersistence?: boolean
}

const headingIdFor = (step: number) => `step-${step}-heading`

export function DealSubmissionFlow({
  mode = 'create',
  initialData = null,
  onSubmit,
  isLoading = false,
  submitButtonLabel,
  successScreenTitle,
  successScreenStatusText,
  successNextSteps,
  enableDraftPersistence = true,
}: DealSubmissionFlowProps) {
  const isEditMode = mode === 'edit'
  const navigate = useNavigate()
  const { projectId } = useParams<{ projectId?: string }>()
  const draftData =
    !isEditMode && enableDraftPersistence ? loadFormDraft() : null
  const initialFormData =
    initialData ?? draftData?.data ?? getDefaultDealFormData()

  const [formData, setFormData] = useState<DealFormData>(initialFormData)
  const [originalFormData, setOriginalFormData] =
    useState<DealFormData>(initialFormData)

  const steps = useMemo(() => {
    return getStepsForSalesType(formData.salesType)
  }, [formData.salesType])

  const FINAL_STEP_ID = steps[steps.length - 1]?.id

  const NON_SUBMIT_STEP_IDS = steps
    .filter((step) => step.id !== FINAL_STEP_ID)
    .map((step) => step.id)

  const draftCompletedSteps = draftData?.completedSteps?.filter((step) =>
    steps.some((s) => s.id === step),
  )
  const initialCompletedSteps =
    initialData && isEditMode ? NON_SUBMIT_STEP_IDS : draftCompletedSteps ?? []

  const [currentStep, setCurrentStep] = useState(1)
  const [completedSteps, setCompletedSteps] = useState<number[]>(
    initialCompletedSteps,
  )
  const [showSuccessScreen, setShowSuccessScreen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSavingDraft, setIsSavingDraft] = useState(false)
  const [validationError, setValidationError] = useState<string | null>(null)
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>(
    [],
  )
  const [errorHeading, setErrorHeading] = useState<string | null>(null)
  const [formInstanceId, setFormInstanceId] = useState(0)
  const [dealId, setDealId] = useState<string | null>(projectId ?? null)
  const [isSubmittingDeal, setIsSubmittingDeal] = useState(false)
  const { showToast } = useToast()

  useEffect(() => {
    if (projectId) setDealId(projectId)
  }, [projectId])

  useEffect(() => {
    if (!isEditMode || !initialData) return
    setFormData(initialData)
    setOriginalFormData(initialData)
    setCompletedSteps(NON_SUBMIT_STEP_IDS)
    setCurrentStep(1)
    setShowSuccessScreen(false)
    setValidationError(null)
    setErrorHeading(null)
    setIsSubmitting(false)
    setFormInstanceId((prev) => prev + 1)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData, isEditMode])

  const isDirty = useMemo(() => {
    return !isEqual(formData, originalFormData)
  }, [formData, originalFormData])

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      isDirty &&
      !showSuccessScreen &&
      !isSavingDraft &&
      !isSubmitting &&
      !isSubmittingDeal &&
      currentLocation.pathname !== nextLocation.pathname,
  )

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty && !showSuccessScreen) {
        e.preventDefault()
        e.returnValue = ''
      }
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [isDirty, showSuccessScreen])

  const handleFormDataUpdate = useCallback(
    (updatedData: Partial<DealFormData>) =>
      setFormData((prev) => ({ ...prev, ...updatedData })),
    [],
  )
  const handleStepBack = useCallback(() => {
    if (currentStep > 1) {
      setValidationError(null)
      setValidationErrors([])
      setErrorHeading(null)

      let prevStep = currentStep - 1
      let foundVisibleStep = false
      for (let i = prevStep; i >= 1; i--) {
        const stepExists = steps.some((s) => s.id === i)
        if (stepExists) {
          prevStep = i
          foundVisibleStep = true
          break
        }
      }

      if (foundVisibleStep) setCurrentStep(prevStep)
    }
  }, [currentStep, steps])

  const handleStepComplete = useCallback(
    (stepData: Partial<DealFormData>) => {
      const updatedFormData = { ...formData, ...stepData }
      setFormData(updatedFormData)
      setValidationError(null)
      setValidationErrors([])
      setErrorHeading(null)
      if (!completedSteps.includes(currentStep))
        setCompletedSteps((prev) => [...prev, currentStep])

      const currentSteps = getStepsForSalesType(updatedFormData.salesType)

      let nextStep = currentStep + 1
      let foundVisibleStep = false
      for (let i = nextStep; i <= currentSteps.length + 10; i++) {
        const stepExists = currentSteps.some((s) => s.id === i)
        if (stepExists) {
          nextStep = i
          foundVisibleStep = true
          break
        }
      }

      if (foundVisibleStep) setCurrentStep(nextStep)
    },
    [formData, currentStep, completedSteps],
  )

  const handleValidationError = useCallback((errorMessage: string) => {
    if (!errorMessage) {
      setValidationError(null)
      setValidationErrors([])
      setErrorHeading(null)
      return
    }
    setErrorHeading('Action required')
    setValidationError(errorMessage)
    setValidationErrors([])
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [])

  const currentStepData = steps.find((s) => s.id === currentStep)
  const CurrentStepComponent = currentStepData?.component

  const requestChangesForStep = useMemo(() => {
    return getRejectReasonsForStep(formData.rejectReasons, currentStep)
  }, [formData.rejectReasons, currentStep])

  const handleValidationErrors = useCallback(
    (errors: ValidationError[]) => {
      const requestChangesMapped = Object.entries(requestChangesForStep).map(
        ([id]) => ({
          id,
          label: getFieldLabel(id),
        }),
      )

      const combinedErrors = [...errors]
      requestChangesMapped.forEach((rc) => {
        if (!combinedErrors.some((e) => e.id === rc.id)) {
          combinedErrors.push(rc)
        }
      })

      if (combinedErrors.length === 0) {
        setValidationErrors([])
        setValidationError(null)
        setErrorHeading(null)
        return
      }

      const heading = `${combinedErrors.length} required field${
        combinedErrors.length > 1 ? 's' : ''
      } need your attentions`
      setErrorHeading(heading)
      setValidationErrors(combinedErrors)
      setValidationError(
        'Please complete or correct the following fields before continuing.',
      )
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    [requestChangesForStep],
  )

  useEffect(() => {
    const hasRequestChanges = Object.keys(requestChangesForStep).length > 0
    if (hasRequestChanges && !showSuccessScreen) {
      handleValidationErrors([])
    }
  }, [requestChangesForStep, showSuccessScreen, handleValidationErrors])

  const scrollToField = (fieldId: string) => {
    if (fieldId === 'assetManagerWallet' || fieldId === 'hasAssetManager') {
      const fundAllocationStep = steps.find(
        (s) => s.title === 'Fund Allocation',
      )
      if (fundAllocationStep && currentStep !== fundAllocationStep.id) {
        setCurrentStep(fundAllocationStep.id)
        setValidationError(null)
        setValidationErrors([])
        setErrorHeading(null)
      }
    }

    const documentsFieldIds = ['researchReport', 'pitchDeck']
    const targetFieldId = documentsFieldIds.includes(fieldId)
      ? 'documents'
      : fieldId

    const element = document.getElementById(targetFieldId)
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }

  useEffect(() => {
    if (showSuccessScreen) return
    const scrollToHeading = () => {
      const el = document.getElementById(headingIdFor(currentStep))
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      else window.scrollTo({ top: 0, behavior: 'smooth' })
    }
    const timeoutId = setTimeout(scrollToHeading, 100)
    return () => clearTimeout(timeoutId)
  }, [currentStep, showSuccessScreen])

  const timestampLabel = isEditMode ? 'Updated at:' : 'Submitted at:'
  const successHeading = successScreenTitle
    ? successScreenTitle
    : isEditMode
    ? 'Deal updated'
    : 'Submission received'
  const normalizedDealState = normalizeDealState(formData.dealState)
  const formattedDealState = formatDealStateLabel(normalizedDealState)
  const successStatusText = successScreenStatusText
    ? successScreenStatusText
    : normalizedDealState === 'SUBMITTED'
    ? 'Under review'
    : normalizedDealState === 'REQUESTED_CHANGE'
    ? 'Revision required'
    : formattedDealState || 'Draft'
  const defaultNextSteps: ReactNode[] = isEditMode
    ? [
        'Our team will review the updated information.',
        'You will be notified if additional details are required.',
      ]
    : [
        'Our team will review your submission.',
        'You will receive an email when the status is updated or if additional information is needed.',
        <>
          If you have any questions, please contact{' '}
          <a
            href="mailto:cs@impossible.finance"
            className="text-brand-primary hover:text-brand-hover underline"
          >
            cs@impossible.finance
          </a>
          .
        </>,
      ]
  const nextSteps = successNextSteps ?? defaultNextSteps

  const SuccessScreen = () => {
    const submittedAt = formData.submittedAt
      ? new Date(formData.submittedAt)
      : new Date()
    const formattedDate = submittedAt.toLocaleString()
    const hideSubmit =
      normalizedDealState === 'SUBMITTED' ||
      normalizedDealState === 'REQUESTED_CHANGE'
    const showBackOnly = normalizedDealState === 'SUBMITTED'
    const isRequestedChange = normalizedDealState === 'REQUESTED_CHANGE'
    const editLabel = isRequestedChange
      ? 'Review & Edit Submission'
      : 'Edit submission'

    const handleSubmitDealNow = async () => {
      if (!dealId) return
      setIsSubmittingDeal(true)
      try {
        await submitDeal(dealId)
        setFormData((prev) => ({
          ...prev,
          dealState: 'SUBMITTED',
        }))
      } catch (error) {
        console.error('Failed to submit deal', error)
      } finally {
        setIsSubmittingDeal(false)
      }
    }
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] space-y-8">
        <div className="text-center space-y-4">
          {isRequestedChange ? (
            <CircleAlert className="text-amber-500 mx-auto h-11 w-11" />
          ) : (
            <CheckCircle className="text-green-500 mx-auto h-11 w-11" />
          )}
          <h1 className="text-3xl font-bold text-neutral-900">
            {isRequestedChange
              ? 'Submission Requires Revision'
              : successHeading}
          </h1>
        </div>
        <Card className="w-full">
          <CardContent className="p-6 space-y-4">
            {!isRequestedChange && (
              <div className="flex items-center space-x-2">
                <span className="text-sm text-neutral-600">
                  {timestampLabel}
                </span>
                <span className="font-medium text-neutral-900 text-sm">
                  {formattedDate}
                </span>
              </div>
            )}
            <div className="flex items-center space-x-2">
              <span className="text-sm text-neutral-600">Status:</span>
              <span className="font-medium text-neutral-900 text-sm">
                {successStatusText}
              </span>
            </div>
            <hr className="border-neutral-200" />
            <div className="space-y-4">
              <h2 className="text-xl font-bold text-neutral-900">Next steps</h2>
              {isRequestedChange ? (
                <div className="space-y-3 text-neutral-600 text-sm">
                  <p className="leading-relaxed">
                    Our team has reviewed your submission and identified some
                    fields that need to be updated.
                  </p>
                  <ul className="space-y-3 list-disc list-inside">
                    <li className="leading-relaxed">
                      Please review the highlighted sections
                    </li>
                    <li className="leading-relaxed">
                      Make the required changes
                    </li>
                    <li className="leading-relaxed">Resubmit once completed</li>
                    <li className="leading-relaxed">
                      If you have any questions, please contact{' '}
                      <a
                        href="mailto:cs@impossible.finance"
                        className="text-brand-primary hover:text-brand-primary/80 underline"
                      >
                        cs@impossible.finance
                      </a>
                      .
                    </li>
                  </ul>
                </div>
              ) : (
                <ul className="space-y-3 list-disc list-inside">
                  {nextSteps.map((step, index) => (
                    <li
                      key={index}
                      className="text-neutral-600 text-sm leading-relaxed"
                    >
                      {step}
                    </li>
                  ))}
                </ul>
              )}
              <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center sm:justify-center pt-2">
                {showBackOnly ? (
                  <Button
                    variant="outline"
                    className="flex h-[42px] w-full sm:w-[40%] items-center justify-center bg-transparent px-5 border-brand-primary text-brand-primary hover:border-brand-hover hover:text-brand-hover hover:bg-brand-subtleBG"
                    onClick={() => navigate('/deals')}
                  >
                    Back to Dashboard
                  </Button>
                ) : (
                  <>
                    <Button
                      variant="outline"
                      disabled={!dealId && !isEditMode}
                      className="flex h-[42px] w-full sm:w-[40%] items-center justify-center bg-transparent px-5 border-brand-primary text-brand-primary hover:border-brand-hover hover:text-brand-hover hover:bg-brand-subtleBG disabled:border-neutral-200 disabled:text-neutral-400 disabled:hover:bg-transparent"
                      onClick={() => {
                        if (!isEditMode && dealId) {
                          navigate(`/deals/${dealId}/edit`)
                          return
                        }
                        setShowSuccessScreen(false)
                        setCurrentStep(1)
                        window.scrollTo({ top: 0, behavior: 'smooth' })
                      }}
                    >
                      {editLabel}
                    </Button>
                    {!hideSubmit ? (
                      <Button
                        variant="default"
                        disabled={!dealId || isSubmittingDeal}
                        className="flex h-[42px] w-full sm:w-[40%] items-center justify-center px-5 sm:ml-4 bg-brand-primary hover:bg-brand-hover text-white disabled:bg-neutral-200 disabled:text-neutral-500"
                        onClick={handleSubmitDealNow}
                      >
                        {isSubmittingDeal ? 'Submitting...' : 'Submit'}
                      </Button>
                    ) : null}
                  </>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  const handleSubmitDeal = async (
    finalStepData: Partial<DealFormData> = {},
  ) => {
    if (showSuccessScreen) return

    const submissionFormData = { ...formData, ...finalStepData }
    const missingFields: ValidationError[] = []

    if (
      submissionFormData.hasAssetManager === 'yes' &&
      !submissionFormData.assetManagerWallet
    ) {
      missingFields.push({
        id: 'assetManagerWallet',
        label: 'Asset Manager Wallet Address',
      })
    }

    if (missingFields.length > 0) {
      setErrorHeading('Action required before submission')
      setValidationError(
        'The asset manager wallet address is required. Add the wallet address to proceed.',
      )
      setValidationErrors(missingFields)
      window.scrollTo({ top: 0, behavior: 'smooth' })
      return
    }

    setIsSubmitting(true)
    try {
      if (isEditMode) {
        if (!dealId) throw new Error('Missing deal id')
        const payload = mapFormToPayload(submissionFormData)
        if (onSubmit) await onSubmit(payload, submissionFormData)

        await submitDeal(dealId)
        const submittedAt = new Date().toISOString()
        setFormData((prev) => ({
          ...prev,
          ...finalStepData,
          submittedAt,
          submissionStatus: 'submitted',
          dealState: 'SUBMITTED',
        }))
        setCompletedSteps(NON_SUBMIT_STEP_IDS)
        setShowSuccessScreen(true)
        window.scrollTo({ top: 0, behavior: 'smooth' })
        return
      }

      const payload = mapFormToPayload(submissionFormData)
      const submissionResult = await postDeal(payload)
      const newDealId = extractDealId(submissionResult)
      if (!newDealId) throw new Error('Missing deal id after create')
      await submitDeal(newDealId)
      const submittedAt = new Date().toISOString()
      clearFormDraft()
      setDealId(newDealId)
      setFormData((prev) => ({
        ...prev,
        ...finalStepData,
        submittedAt,
        submissionStatus: 'submitted',
        dealState: 'SUBMITTED',
      }))
      setCompletedSteps(NON_SUBMIT_STEP_IDS)
      setShowSuccessScreen(true)
      window.scrollTo({ top: 0, behavior: 'smooth' })
    } catch (error) {
      console.error('Submit failed:', error)
      const { message } = formatSubmissionError(error, isEditMode)
      showToast({
        message: message ?? 'Failed to submit. Please try again.',
        type: 'error',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleSaveDraftOnly = async (skipNavigate = false) => {
    if (showSuccessScreen) return false
    setIsSavingDraft(true)
    try {
      if (isEditMode) {
        const payload = mapFormToPayload(formData)
        if (onSubmit) await onSubmit(payload, formData)
        showToast({
          message: 'Deal saved successfully',
          type: 'success',
        })
        setOriginalFormData(formData)
        return true
      } else {
        const payload = mapFormToPayload(formData)
        const submissionResult = await postDeal(payload)
        const newDealId = extractDealId(submissionResult)
        if (newDealId) {
          clearFormDraft()
          showToast({
            message: 'Deal saved successfully',
            type: 'success',
          })
          if (!skipNavigate) {
            setOriginalFormData(formData)
            navigate(`/deals/${newDealId}/edit`)
          } else {
            setOriginalFormData(formData)
          }
          return true
        }
      }
    } catch (error) {
      console.error('Draft save failed:', error)
      showToast({
        message: 'Failed to save deal. Please try again.',
        type: 'error',
      })
    } finally {
      setIsSavingDraft(false)
    }
    return false
  }

  if (isEditMode && isLoading)
    return (
      <div className="min-h-screen bg-neutral-50">
        <div className="max-w-6xl mx-auto px-4 py-8">
          <div className="animate-pulse rounded-lg border border-neutral-200 bg-white p-8 text-neutral-500">
            Loading deal details...
          </div>
        </div>
      </div>
    )

  if (isEditMode && !isLoading && !initialData)
    return (
      <div className="min-h-screen bg-neutral-50">
        <div className="max-w-6xl mx-auto px-4 py-8">
          <div className="rounded-lg border border-status-error/30 bg-white p-8 text-status-error">
            Unable to load the requested deal. Please refresh or try again
            later.
          </div>
        </div>
      </div>
    )

  const navCompletedSteps = showSuccessScreen
    ? steps.map((step) => step.id)
    : completedSteps
  const navCurrentStep = showSuccessScreen ? 0 : currentStep
  const saveDraftLabel = isEditMode ? 'Save' : 'Save Draft'
  const errorBody = validationError ?? 'Please complete all required fields.'
  const errorTitle = errorHeading ?? 'Action required'

  return (
    <div className="min-h-screen bg-neutral-50">
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex justify-center gap-8">
          <aside className="hidden lg:block w-[220px] shrink-0">
            <div className="sticky top-8">
              <StepNavigation
                steps={steps}
                currentStep={navCurrentStep}
                completedSteps={navCompletedSteps}
              />
            </div>
          </aside>

          <div className="lg:hidden w-full mb-6">
            <StepNavigation
              steps={steps}
              currentStep={navCurrentStep}
              completedSteps={navCompletedSteps}
              mobile
            />
          </div>

          <main className="flex-1 max-w-3xl">
            <div className={!showSuccessScreen ? 'pt-2' : ''}>
              {!showSuccessScreen &&
                (validationError ||
                  Object.keys(requestChangesForStep).length > 0) && (
                  <div className="mb-6 p-4 border rounded-lg bg-red-50 border-red-200">
                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        <CircleAlert className="h-5 w-5 text-status-error" />
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-status-error">
                          {errorTitle}
                        </h3>
                        <div className="mt-2 text-xs text-status-error">
                          {errorBody}
                        </div>
                        {validationErrors.length > 0 && (
                          <ul className="mt-3 space-y-1 list-none p-0">
                            {validationErrors.map((err) => (
                              <li key={err.id}>
                                <button
                                  type="button"
                                  onClick={() => scrollToField(err.id)}
                                  className="group text-xs hover:opacity-80 transition-opacity flex items-center text-left text-status-error"
                                >
                                  <span className="mr-2">•</span>
                                  <span className="group-hover:underline text-left">
                                    {err.label}
                                  </span>
                                </button>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>
                  </div>
                )}

              {!showSuccessScreen && (
                <div className="mb-8">
                  <div className="flex items-center justify-between gap-4 mb-2">
                    <h2
                      id={headingIdFor(currentStep)}
                      className="font-bold text-neutral-950 text-3xl scroll-mt-24"
                    >
                      {steps.find((s) => s.id === currentStep)?.title}
                    </h2>
                    <Button
                      variant="outline"
                      disabled={isSavingDraft || isSubmitting || !isDirty}
                      className="h-[38px] px-4 border-brand-primary text-brand-primary shrink-0"
                      onClick={() => handleSaveDraftOnly()}
                    >
                      {isSavingDraft ? 'Saving...' : saveDraftLabel}
                    </Button>
                  </div>
                  <p className="text-neutral-600 text-base">
                    {steps.find((s) => s.id === currentStep)?.description}
                  </p>
                </div>
              )}

              {showSuccessScreen ? (
                <SuccessScreen />
              ) : currentStep === FINAL_STEP_ID &&
                formData.salesType === ERC4626_DEAL_TYPE ? (
                <StepErc4626Vault
                  key={`step-${FINAL_STEP_ID}-${formInstanceId}`}
                  formData={formData}
                  onComplete={handleStepComplete}
                  onUpdate={handleFormDataUpdate}
                  onBack={handleStepBack}
                  onSubmitDeal={handleSubmitDeal}
                  isSubmitting={isSubmitting}
                  submitLabel="Submit"
                  inProgressLabel="Submitting..."
                  onValidationErrors={handleValidationErrors}
                  requestChanges={requestChangesForStep}
                />
              ) : currentStep === FINAL_STEP_ID &&
                isV1ContractDealType(formData.salesType) ? (
                <StepV1Deposit
                  key={`step-${FINAL_STEP_ID}-${formInstanceId}`}
                  formData={formData}
                  onComplete={handleStepComplete}
                  onUpdate={handleFormDataUpdate}
                  onBack={handleStepBack}
                  onSubmitDeal={handleSubmitDeal}
                  isSubmitting={isSubmitting}
                  submitLabel="Submit"
                  inProgressLabel="Submitting..."
                  onValidationErrors={handleValidationErrors}
                  requestChanges={requestChangesForStep}
                />
              ) : currentStep === FINAL_STEP_ID ? (
                <StepAccessoryServices
                  key={`step-${FINAL_STEP_ID}-${formInstanceId}`}
                  formData={formData}
                  onComplete={handleStepComplete}
                  onUpdate={handleFormDataUpdate}
                  onBack={handleStepBack}
                  headingId={headingIdFor(currentStep)}
                  onSubmitDeal={handleSubmitDeal}
                  isSubmitting={isSubmitting}
                  submitLabel="Submit"
                  inProgressLabel="Submitting..."
                  onValidationErrors={handleValidationErrors}
                  requestChanges={requestChangesForStep}
                />
              ) : CurrentStepComponent ? (
                <CurrentStepComponent
                  key={`step-${currentStep}-${formInstanceId}`}
                  formData={formData}
                  onComplete={handleStepComplete}
                  onUpdate={handleFormDataUpdate}
                  onBack={handleStepBack}
                  headingId={headingIdFor(currentStep)}
                  onValidationError={handleValidationError}
                  onValidationErrors={handleValidationErrors}
                  requestChanges={requestChangesForStep}
                />
              ) : null}
            </div>
          </main>
        </div>
      </div>

      <Dialog
        open={blocker.state === 'blocked'}
        onOpenChange={() => blocker.reset?.()}
      >
        <DialogContent className="max-w-[480px] p-8" hideClose={false}>
          <div className="flex flex-col items-center">
            <div className="mb-6">
              <TriangleAlert className="w-16 h-16 text-yellow-500" />
            </div>
            <div className="w-full text-left mb-8">
              <p className="text-base font-normal text-neutral-600 leading-relaxed">
                You have unsaved changes. If you leave this page now, your
                latest edits will not be saved.
              </p>
            </div>
            <div className="flex gap-4 w-full">
              <Button
                variant="outline"
                className="flex-1 h-12 rounded-md text-brand-primary border-brand-primary font-medium"
                onClick={() => blocker.proceed?.()}
              >
                Leave without saving
              </Button>
              <Button
                variant="brand"
                className="flex-1 h-12 rounded-md bg-brand-primary font-medium"
                onClick={async () => {
                  const success = await handleSaveDraftOnly(true)
                  if (success) {
                    blocker.proceed?.()
                  }
                }}
                disabled={isSavingDraft}
              >
                {isSavingDraft ? 'Saving...' : saveDraftLabel}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
